import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO
        title="Sign In"
        description="Sign in to RED.FLAG — the AI-powered personal finance coach that tracks expenses, alerts you on overspending, and helps you hit savings goals."
        path="/login"
      />
      {/* Left brand panel */}
      <div className="bg-black text-white p-10 flex flex-col justify-between relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[var(--alert-red)] flex items-center justify-center font-display font-black">!</div>
          <span className="font-display font-black tracking-tighter text-xl">RED.FLAG</span>
        </div>
        <div>
          <div className="overline text-white/60 mb-4">PERSONAL FINANCE COACH</div>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tighter leading-[0.9]">
            SPEND <br />
            <span className="text-[var(--alert-red)]">SMART.</span> <br />
            GET FLAGGED <br />
            EARLY.
          </h1>
          <p className="mt-6 font-mono text-sm max-w-md text-white/70">
            Track every expense. Set category and overall budgets. The dashboard turns
            <span className="text-[var(--alert-red)]"> red </span>
            the moment you cross the line.
          </p>
        </div>
        <div className="overline text-white/40">A QUIET DASHBOARD UNTIL IT ISN&apos;T.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="overline mb-2">SIGN IN</div>
          <h2 className="font-display font-black text-4xl tracking-tighter mb-8">
            Welcome back.
          </h2>

          <label className="block mb-6">
            <div className="overline mb-2">EMAIL</div>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
            />
          </label>

          <label className="block mb-6">
            <div className="overline mb-2">PASSWORD</div>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
            />
          </label>

          {err && (
            <div data-testid="login-error" className="mb-4 border-2 border-[var(--alert-red)] bg-[var(--alert-wash)] text-[var(--alert-red)] p-3 font-mono text-sm">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50"
          >
            {loading ? "SIGNING IN…" : "SIGN IN →"}
          </button>

          <div className="mt-6 font-mono text-sm">
            No account?{" "}
            <Link to="/register" data-testid="link-to-register" className="underline font-bold">
              CREATE ONE
            </Link>
          </div>

          <div className="mt-2 font-mono text-sm">
            <Link to="/forgot-password" data-testid="link-to-forgot" className="underline">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
